import React, { useState, useEffect, useRef } from 'react';
import {
  Truck, MapPin, CheckCircle, AlertTriangle, ArrowLeft, Play, Flag, Check,
  Clock, Globe, ShieldCheck, ChevronRight, ChevronLeft, Calendar, User, Phone, Mail, Award,
  Navigation, DollarSign, Building, X, Camera, Plus, FileText, Image as ImageIcon,
  PenTool, Package, CreditCard, Lock, CheckSquare, Trash2, ChevronDown, RefreshCw,
  Pause, PlayCircle, Map, List, Copy, Layers, Settings
} from 'lucide-react';
import MapView from '../components/MapView';

function formatDateFormatted(date) {
  const frenchDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const frenchMonths = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
  return `${frenchDays[date.getDay()]} ${date.getDate()} ${frenchMonths[date.getMonth()]} ${date.getFullYear()}`;
}

function formatDateISO(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDurationSec(totalSec = 0) {
  const h = Math.floor((totalSec || 0) / 3600);
  const m = Math.floor(((totalSec || 0) % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatTimerHHMMSS(totalSec = 0) {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function calcHaversineDistanceKm(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return parseFloat((R * c).toFixed(1));
}

function calculateTruckPeakPayload(truckInfo, jobStopsList) {
  const maxWeight = parseFloat(truckInfo?.max_payload_weight_kg || 1200);
  const maxVolume = parseFloat(truckInfo?.max_payload_volume_m3 || 14.0);

  let currentWeight = 0;
  let currentVolume = 0;
  let peakWeight = 0;
  let peakVolume = 0;

  for (const s of jobStopsList) {
    const w = parseFloat(s.weight_kg || 0);
    const v = parseFloat(s.volume_m3 || 0.5);
    currentWeight += w;
    currentVolume += v;

    if (currentWeight > peakWeight) peakWeight = currentWeight;
    if (currentVolume > peakVolume) peakVolume = currentVolume;
  }

  return {
    peakWeight: Math.round(peakWeight),
    maxWeight: Math.round(maxWeight),
    peakVolume: parseFloat(peakVolume.toFixed(1)),
    maxVolume: parseFloat(maxVolume.toFixed(1)),
    isOverweight: peakWeight > maxWeight,
    isOvervolume: peakVolume > maxVolume
  };
}

function calcHubToStopDriveMins(hub, stop, departureTimeStr) {
  if (!hub || !stop) return 15;
  const km = calcHaversineDistanceKm(hub.latitude, hub.longitude, stop.latitude, stop.longitude);
  return Math.max(3, Math.round(km * 2));
}

function calcLegDriveTimeMins(stopA, stopB) {
  if (!stopA || !stopB) return 10;
  const km = calcHaversineDistanceKm(stopA.latitude, stopA.longitude, stopB.latitude, stopB.longitude);
  return Math.max(2, Math.round(km * 2));
}

function calcStopToHubDriveMins(stop, hub, returnTimeStr) {
  if (!stop || !hub) return 15;
  const km = calcHaversineDistanceKm(stop.latitude, stop.longitude, hub.latitude, hub.longitude);
  return Math.max(3, Math.round(km * 2));
}

function calculateReturnTime(departureTimeStr, displaySec) {
  const [hStr, mStr] = (departureTimeStr || '07:30').split(':');
  const depSec = (parseInt(hStr || 7) * 3600) + (parseInt(mStr || 30) * 60);
  const totalSec = (depSec + (displaySec || 3600)) % (24 * 3600);
  const retH = Math.floor(totalSec / 3600);
  const retM = Math.floor((totalSec % 3600) / 60);
  return `${String(retH).padStart(2, '0')}:${String(retM).padStart(2, '0')}`;
}

function calcDepartureTimeStr(arrivalTimeFormatted, serviceMins) {
  const [hStr, mStr] = (arrivalTimeFormatted || '08:00').split(':');
  const totalMins = (parseInt(hStr || 8) * 60) + parseInt(mStr || 0) + parseInt(serviceMins || 10);
  const depH = Math.floor(totalMins / 60) % 24;
  const depM = totalMins % 60;
  return `${String(depH).padStart(2, '0')}:${String(depM).padStart(2, '0')}`;
}

function formatLocalTimeHHMM(val) {
  if (!val) return '--:--';
  const str = String(val).trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(str)) {
    return str.slice(0, 5);
  }
  let isoStr = str;
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(str)) {
    isoStr = str.replace(' ', 'T') + 'Z';
  }
  const d = new Date(isoStr);
  if (!isNaN(d.getTime())) {
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }
  return str.slice(0, 5);
}

function renderStopRealTimeWindows(stop, client, arrivalTimeFormatted) {
  let windows = [];
  if (stop.time_windows && Array.isArray(stop.time_windows) && stop.time_windows.length > 0) {
    windows = stop.time_windows;
  } else if (client?.time_window_start && client?.time_window_end) {
    windows = [{ start: client.time_window_start.slice(0,5), end: client.time_window_end.slice(0,5) }];
  } else {
    windows = [{ start: '08:00', end: '18:00' }];
  }

  const arrTime = stop.actual_arrival ? formatLocalTimeHHMM(stop.actual_arrival) : arrivalTimeFormatted;
  const arrMins = (parseInt(arrTime.split(':')[0] || 8) * 60) + parseInt(arrTime.split(':')[1] || 0);

  let matchedIndex = -1;
  windows.forEach((tw, idx) => {
    const sMins = (parseInt((tw.start || '08:00').split(':')[0] || 8) * 60) + parseInt((tw.start || '08:00').split(':')[1] || 0);
    const eMins = (parseInt((tw.end || '18:00').split(':')[0] || 18) * 60) + parseInt((tw.end || '18:00').split(':')[1] || 0);
    if (arrMins >= sMins && arrMins <= eMins) {
      matchedIndex = idx;
    }
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center' }}>
      {windows.map((tw, idx) => {
        let pillBg = 'rgba(239, 68, 68, 0.15)';
        let pillColor = '#ef4444';
        let pillBorder = '1px solid #ef4444';

        if (matchedIndex !== -1) {
          if (idx === matchedIndex) {
            pillBg = 'rgba(34, 197, 94, 0.2)';
            pillColor = '#22c55e';
            pillBorder = '1px solid #22c55e';
          } else {
            pillBg = '#1e293b';
            pillColor = '#64748b';
            pillBorder = '1px solid #334155';
          }
        }

        return (
          <span
            key={idx}
            style={{
              background: pillBg,
              color: pillColor,
              border: pillBorder,
              fontSize: '0.68rem',
              fontWeight: '700',
              padding: '1px 5px',
              borderRadius: '4px',
              whiteSpace: 'nowrap'
            }}
          >
            {(tw.start || '08:00').slice(0, 5)} - {(tw.end || '18:00').slice(0, 5)}
          </span>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------
// SIGNATURE CANVAS COMPONENT
// ----------------------------------------------------
function SignaturePadCanvas({ onSave, onCancel, title = "Signature", subtitle = "", onCopyFirstStopSig = null }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0284c7';
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn) return;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: '0.88rem', fontWeight: '800', color: '#38bdf8' }}>{title}</span>
          {subtitle && <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '1px' }}>{subtitle}</div>}
        </div>

        {onCopyFirstStopSig && (
          <button
            type="button"
            onClick={onCopyFirstStopSig}
            style={{
              background: 'rgba(56, 189, 248, 0.15)',
              border: '1px solid #38bdf8',
              color: '#38bdf8',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '0.72rem',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer'
            }}
            title="Copier la signature enregistrée sur le 1er stop"
          >
            <Copy size={12} />
            <span>Copier sig. 1er stop</span>
          </button>
        )}
      </div>

      <div style={{ background: '#ffffff', borderRadius: '8px', overflow: 'hidden', border: '2px dashed #94a3b8', position: 'relative' }}>
        <canvas
          ref={canvasRef}
          width={380}
          height={170}
          style={{ width: '100%', height: '170px', display: 'block', cursor: 'crosshair', touchAction: 'none' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <span style={{ position: 'absolute', bottom: '8px', right: '10px', fontSize: '0.68rem', color: '#94a3b8', pointerEvents: 'none' }}>
          Signer avec le doigt ou la souris
        </span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          type="button"
          onClick={clearCanvas}
          style={{ background: '#334155', color: '#cbd5e1', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}
        >
          Effacer
        </button>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={!hasDrawn}
            onClick={handleSave}
            style={{
              background: hasDrawn ? '#22c55e' : '#334155',
              color: '#ffffff',
              border: 'none',
              padding: '6px 16px',
              borderRadius: '6px',
              fontWeight: '800',
              fontSize: '0.75rem',
              cursor: hasDrawn ? 'pointer' : 'not-allowed'
            }}
          >
            Enregistrer Signature
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------
// MAIN DRIVER APP PAGE
// ----------------------------------------------------
export default function DriverAppPage({ driverId = 1, serverUrl = '', onSwitchServerOrDriver = null }) {
  const baseUrl = (serverUrl || localStorage.getItem('loops_driver_server_url') || '').replace(/\/+$/, '');
  const apiFetch = (url, options = {}) => {
    const fullUrl = url.startsWith('http://') || url.startsWith('https://')
      ? url
      : `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
    return fetch(fullUrl, options);
  };
  const [driver, setDriver] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stops, setStops] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [objects, setObjects] = useState([]);
  const [allJobAttachments, setAllJobAttachments] = useState([]);
  const [jobPauses, setJobPauses] = useState([]);
  
  // Date Selector State (defaults to current day)
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const selectedDateISO = formatDateISO(currentDate);

  const handlePrevDay = () => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 1);
      return d;
    });
    setSelectedJobId(null);
  };

  const handleNextDay = () => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 1);
      return d;
    });
    setSelectedJobId(null);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedJobId(null);
  };

  const [selectedJobId, setSelectedJobId] = useState(null);
  const [activeStopModal, setActiveStopModal] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'
  
  // Non-blocking Pause state
  const [isPaused, setIsPaused] = useState(false);
  const [pauseStartTime, setPauseStartTime] = useState(null);
  const [pauseElapsedSec, setPauseElapsedSec] = useState(0);
  
  // Stop popup form & sub-modal states
  const [driverNotes, setDriverNotes] = useState('');
  const [paymentType, setPaymentType] = useState('none');
  const [amountPaid, setAmountPaid] = useState('');
  const [stopAttachments, setStopAttachments] = useState([]);
  const [stopReturnTickets, setStopReturnTickets] = useState([]);
  
  // Sub-action modals ('action_menu' | 'photo' | 'driver_sig' | 'recipient_sig' | 'return_ticket' | 'problem_reasons')
  const [activeActionModal, setActiveActionModal] = useState(null);
  
  // Return ticket tabs ('returned' = consignes / retours, 'order_items' = articles commande)
  const [returnTicketTab, setReturnTicketTab] = useState('returned');
  
  // Problem reasons multi-select state
  const [selectedProblemReasons, setSelectedProblemReasons] = useState([]);
  const commonProblemReasons = [
    { id: 'absent', label: 'Client absent / Fermé', icon: '🚫' },
    { id: 'wrong_address', label: 'Adresse introuvable / Erronée', icon: '📍' },
    { id: 'refused', label: 'Refus de livraison par le destinataire', icon: '🛑' },
    { id: 'damaged', label: 'Colis ou emballage endommagé', icon: '📦' },
    { id: 'missing_items', label: 'Marchandise manquante / incomplète', icon: '⚠️' },
    { id: 'access_blocked', label: 'Accès impossible (travaux / barrière)', icon: '🚧' },
    { id: 'payment_missing', label: 'Paiement non disponible', icon: '💳' },
    { id: 'other', label: 'Autre motif', icon: '❓' }
  ];

  // Recipient signature state
  const [recipientContactId, setRecipientContactId] = useState('');
  const [recipientCustomName, setRecipientCustomName] = useState('');

  // Return ticket state
  const [ticketOrderId, setTicketOrderId] = useState('');
  const [ticketLines, setTicketLines] = useState([]);

  // Photo upload state
  const [photoDataUrl, setPhotoDataUrl] = useState('');
  const [photoType, setPhotoType] = useState('other_photo');
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  // Odometer Real Km state
  const [startKm, setStartKm] = useState('');
  const [endKm, setEndKm] = useState('');

  // Live timer for pause
  useEffect(() => {
    let interval = null;
    if (isPaused && pauseStartTime) {
      interval = setInterval(() => {
        const sec = Math.floor((Date.now() - pauseStartTime) / 1000);
        setPauseElapsedSec(sec);
      }, 1000);
    } else {
      setPauseElapsedSec(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPaused, pauseStartTime]);

  // Unblock body scrolling for mobile & desktop Driver App
  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyHeight = document.body.style.height;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevHtmlHeight = document.documentElement.style.height;

    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.height = prevBodyHeight;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.height = prevHtmlHeight;
    };
  }, []);

  const loadDriverData = async (targetDateStr = null) => {
    try {
      const dateStr = targetDateStr || selectedDateISO;
      const res = await apiFetch(`/api/bootstrap?date=${dateStr}&window_days=0`);
      const data = await res.json();
      const allDrivers = data.drivers || [];
      const currentDriver = allDrivers.find(d => d.id === parseInt(driverId)) || allDrivers[0] || {
        id: 1, first_name: 'Chauffeur', last_name: 'Inconnu', email: 'driver@loops.ch', phone: '+41 79 123 45 67', driving_license: 'B'
      };

      setDriver(currentDriver);
      setTrucks(data.trucks || []);
      setRounds(data.rounds || []);
      setJobs(data.jobs || []);
      setStops(data.stops || []);
      setHubs(data.hubs || []);
      setClients(data.clients || []);
      setClientContacts(data.client_contacts || []);
      setObjects(data.objects || []);
      setAllJobAttachments(data.stop_attachments || []);
      setJobPauses(data.job_pauses || []);
    } catch (err) {
      console.error('Error fetching driver app bootstrap data:', err);
    }
  };

  // Reload data whenever date or driver changes
  useEffect(() => {
    loadDriverData(selectedDateISO);
  }, [selectedDateISO, driverId]);

  // Robust WebSocket Connection with auto-reconnect & cross-event support
  useEffect(() => {
    let ws = null;
    let reconnectTimeout = null;
    let isUnmounted = false;

    const getWsUrl = () => {
      if (baseUrl) {
        try {
          const u = new URL(baseUrl);
          const protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
          return `${protocol}//${u.host}`;
        } catch (e) {}
      }
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = (window.location.port === '3000' || window.location.port === '5173') ? '3001' : (window.location.port || '5000');
      return `${protocol}//${host}:${port}`;
    };

    const connectWs = () => {
      if (isUnmounted) return;
      try {
        ws = new WebSocket(getWsUrl());

        ws.onopen = () => {
          if (!isUnmounted) setWsConnected(true);
        };

        ws.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            if (payload.type === 'REALTIME_DRIVER_UPDATE') {
              const { stop, job } = payload;
              if (stop) {
                setStops(prev => prev.map(s => s.id === stop.id ? { ...s, ...stop } : s));
                setActiveStopModal(prev => prev && prev.id === stop.id ? { ...prev, ...stop } : prev);
              }
              if (job) {
                setJobs(prev => prev.map(j => j.id === job.id ? { ...j, ...job } : j));
              }
            }
            loadDriverData(selectedDateISO);
          } catch (err) {
            console.error('Error parsing WS message:', err);
          }
        };

        ws.onclose = () => {
          if (!isUnmounted) {
            setWsConnected(false);
            reconnectTimeout = setTimeout(connectWs, 3000);
          }
        };

        ws.onerror = () => {
          if (!isUnmounted) {
            setWsConnected(false);
            try { ws.close(); } catch(e){}
          }
        };
      } catch (e) {
        if (!isUnmounted) {
          reconnectTimeout = setTimeout(connectWs, 3000);
        }
      }
    };

    connectWs();

    return () => {
      isUnmounted = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, [driverId, selectedDateISO]);

  const isStopOnSelectedDate = (stop) => {
    const d = selectedDateISO;
    if (!d) return true;

    if (stop.delivery_date) {
      const stopDateStr = String(stop.delivery_date).slice(0, 10);
      return stopDateStr === d;
    }
    if (stop.actual_arrival && String(stop.actual_arrival).startsWith(d)) return true;
    if (stop.estimated_arrival && String(stop.estimated_arrival).startsWith(d)) return true;
    if (stop.real_departure && String(stop.real_departure).startsWith(d)) return true;
    return false;
  };

  const isJobOnSelectedDate = (job) => {
    const d = selectedDateISO;
    if (!d) return true;

    const jobStops = stops.filter(s => s.job_id === job.id);
    if (jobStops.length > 0) {
      return jobStops.some(s => isStopOnSelectedDate(s));
    }
    if (job.real_start && String(job.real_start).startsWith(d)) return true;
    if (job.real_end && String(job.real_end).startsWith(d)) return true;
    if (job.created_at && String(job.created_at).slice(0, 10) === d) return true;
    return false;
  };

  const driverJobs = jobs
    .filter(j => j.driver_id === parseInt(driver?.id || 1))
    .filter(isJobOnSelectedDate);
  const currentJob = jobs.find(j => j.id === selectedJobId) || null;

  // Sync real_start_km and real_end_km from loaded job
  useEffect(() => {
    if (currentJob) {
      setStartKm(currentJob.real_start_km !== null && currentJob.real_start_km !== undefined ? String(currentJob.real_start_km) : '');
      setEndKm(currentJob.real_end_km !== null && currentJob.real_end_km !== undefined ? String(currentJob.real_end_km) : '');
    }
  }, [currentJob?.id, currentJob?.real_start_km, currentJob?.real_end_km]);

  // Automatic Geolocation & Geohistory Tracking
  useEffect(() => {
    if (typeof window === 'undefined' || !navigator.geolocation) return;

    let lastSentTime = 0;
    let lastLat = null;
    let lastLng = null;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, speed, heading, accuracy } = position.coords;
        const now = Date.now();

        let dist = 0;
        if (lastLat && lastLng) {
          dist = calcHaversineDistanceKm(lastLat, lastLng, latitude, longitude) * 1000;
        }

        if (now - lastSentTime > 25000 || dist > 20 || lastSentTime === 0) {
          lastSentTime = now;
          lastLat = latitude;
          lastLng = longitude;

          apiFetch('/api/driver-geohistory', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              driver_id: driver?.id || parseInt(driverId) || null,
              job_id: currentJob?.id || null,
              latitude,
              longitude,
              speed: speed !== null && speed !== undefined ? Math.round(speed * 3.6) : null,
              heading,
              accuracy,
              timestamp: new Date().toISOString()
            })
          }).catch(() => {});
        }
      },
      () => {},
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 5000
      }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, [driver?.id, driverId, currentJob?.id]);

  const isJobStarted = currentJob && (currentJob.status === 'in_progress' || currentJob.status === 'completed' || currentJob.status === 'done');

  // Job stops sorted sequentially by sequence_order (backend ensures clean 1..N order: completed first, then pending)
  const currentJobStops = currentJob
    ? stops.filter(s => s.job_id === currentJob.id).sort((a, b) => {
        const seqA = a.sequence_order !== null && a.sequence_order !== undefined ? parseInt(a.sequence_order) : (a.estimated_index || 0);
        const seqB = b.sequence_order !== null && b.sequence_order !== undefined ? parseInt(b.sequence_order) : (b.estimated_index || 0);
        return seqA - seqB;
      })
    : [];

  const allJobStopsValidated = currentJobStops.length > 0 && currentJobStops.every(s => s.status === 'delivered' || s.status === 'failed');

  // Find first stop with driver signature in this mission
  const firstStopWithDriverSig = currentJobStops.find(s => {
    const att = allJobAttachments.find(a => a.stop_id === s.id && a.attachment_type === 'driver_signature');
    return !!att;
  });
  const firstStopSigUrl = firstStopWithDriverSig
    ? allJobAttachments.find(a => a.stop_id === firstStopWithDriverSig.id && a.attachment_type === 'driver_signature')?.value
    : null;

  // Daily driver stats
  const totalDriverKm = driverJobs.reduce((acc, j) => acc + parseFloat(j.estimated_length_km || j.real_length_km || 0), 0);
  const allDriverStops = stops.filter(s => driverJobs.some(j => j.id === s.job_id) && isStopOnSelectedDate(s));
  const completedDriverStops = allDriverStops.filter(s => s.status === 'delivered' || s.status === 'failed');
  const totalDriverDurationSec = driverJobs.reduce((acc, j) => acc + parseInt(j.estimated_duration_sec || j.real_duration_sec || 0), 0);
  const hours = Math.floor(totalDriverDurationSec / 3600);
  const mins = Math.floor((totalDriverDurationSec % 3600) / 60);

  const handleUpdateJobKm = async (field, value) => {
    if (!currentJob) return;
    const numVal = value !== '' && !isNaN(value) ? parseFloat(value) : null;
    try {
      await apiFetch(`/api/jobs/${currentJob.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: numVal })
      });
      await loadDriverData();
    } catch (err) {
      console.error(`Error updating job ${field}:`, err);
    }
  };

  const handleStartMission = async () => {
    if (!currentJob) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/jobs/${currentJob.id}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ real_start_km: startKm !== '' ? parseFloat(startKm) : null })
      });
      if (res.ok) await loadDriverData();
    } catch (err) {
      console.error('Error starting mission:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFinishMission = async () => {
    if (!currentJob) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/jobs/${currentJob.id}/finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          real_start_km: startKm !== '' ? parseFloat(startKm) : null,
          real_end_km: endKm !== '' ? parseFloat(endKm) : null
        })
      });
      if (res.ok) await loadDriverData();
    } catch (err) {
      console.error('Error finishing mission:', err);
    } finally {
      setLoading(false);
    }
  };

  // Pause Controls
  const handleTogglePause = async () => {
    if (!isPaused) {
      // Start pause
      setIsPaused(true);
      setPauseStartTime(Date.now());
      setPauseElapsedSec(0);
    } else {
      // Resume / complete pause and log real pause
      const durationMins = Math.max(1, Math.round(pauseElapsedSec / 60));
      const firstPendingStop = currentJobStops.find(s => s.status === 'pending');
      
      setLoading(true);
      try {
        const now = new Date();
        const pStart = pauseStartTime ? new Date(pauseStartTime) : new Date(Date.now() - durationMins * 60000);
        const startStr = `${String(pStart.getHours()).padStart(2, '0')}:${String(pStart.getMinutes()).padStart(2, '0')}`;
        const endStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

        await apiFetch('/api/job-pauses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            job_id: currentJob.id,
            before_stop_id: firstPendingStop?.id || null,
            type: 'real',
            start: startStr,
            end: endStr,
            duration_mins: durationMins,
            pause_label: `Pause réelle (${durationMins} min)`
          })
        });
        await loadDriverData();
      } catch (e) {
        console.error('Error saving real pause:', e);
      } finally {
        setIsPaused(false);
        setPauseStartTime(null);
        setPauseElapsedSec(0);
        setLoading(false);
      }
    }
  };

  // Open Stop Popup and load attachments & return tickets
  const handleOpenStopModal = async (stop) => {
    setActiveStopModal(stop);
    setDriverNotes(stop.driver_notes || '');
    setPaymentType(stop.payment_type || 'none');
    setAmountPaid(stop.amount_paid !== undefined && stop.amount_paid !== null ? String(stop.amount_paid) : '');
    setSelectedProblemReasons(stop.problem_reasons ? stop.problem_reasons.split(', ') : []);
    setActiveActionModal(null);
    setStopAttachments([]);
    setStopReturnTickets([]);

    try {
      const [attRes, tRes] = await Promise.all([
        apiFetch(`/api/stops/${stop.id}/attachments`),
        apiFetch(`/api/stops/${stop.id}/return-tickets`)
      ]);
      if (attRes.ok) {
        const attData = await attRes.json();
        setStopAttachments(attData);
      }
      if (tRes.ok) {
        const tData = await tRes.json();
        setStopReturnTickets(tData);
      }
    } catch (e) {
      console.error('Error loading stop details:', e);
    }
  };

  const handleMarkArrival = async (stopId) => {
    if (!currentJob || currentJob.status !== 'in_progress') {
      alert("Veuillez d'abord démarrer la tournée pour pouvoir marquer votre arrivée.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/stops/${stopId}/arrival`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.stop) {
          setStops(prev => prev.map(s => s.id === data.stop.id ? { ...s, ...data.stop } : s));
          setActiveStopModal(prev => prev && prev.id === data.stop.id ? { ...prev, ...data.stop } : prev);
        }
        await loadDriverData();
      }
    } catch (err) {
      console.error('Error marking arrival:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteStop = async (stopId, status, customNotes = null, customReasons = null) => {
    if (!currentJob || currentJob.status !== 'in_progress') {
      alert("Veuillez d'abord démarrer la tournée pour pouvoir valider ce stop.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/stops/${stopId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          driver_notes: customNotes !== null ? customNotes : driverNotes,
          payment_type: paymentType,
          amount_paid: amountPaid ? parseFloat(amountPaid) : 0.00,
          problem_reasons: customReasons !== null ? customReasons : selectedProblemReasons
        })
      });
      if (res.ok) {
        await loadDriverData();
        setDriverNotes('');
        setActiveStopModal(null);
        setActiveActionModal(null);
      }
    } catch (err) {
      console.error('Error completing stop:', err);
    } finally {
      setLoading(false);
    }
  };

  // Upload handler for photos & signatures
  const handleUploadAndAttach = async (base64Data, type, filename = 'attachment') => {
    if (!activeStopModal) return;
    setLoading(true);
    try {
      const folder = type.includes('signature') ? 'signatures' : 'photos';
      const uploadRes = await apiFetch('/api/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: base64Data, filename, folder })
      });
      if (uploadRes.ok) {
        const uploadData = await uploadRes.json();
        const attRes = await apiFetch(`/api/stops/${activeStopModal.id}/attachments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attachment_type: type, value: uploadData.url })
        });
        if (attRes.ok) {
          const newAtt = await attRes.json();
          setStopAttachments(prev => [...prev, newAtt]);
          setAllJobAttachments(prev => [...prev, newAtt]);
        }
      }
    } catch (err) {
      console.error('Error uploading attachment:', err);
    } finally {
      setLoading(false);
      setActiveActionModal(null);
    }
  };

  // Copy driver signature from first stop
  const handleCopyFirstStopSignature = async () => {
    if (!firstStopSigUrl || !activeStopModal) return;
    setLoading(true);
    try {
      const attRes = await apiFetch(`/api/stops/${activeStopModal.id}/attachments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attachment_type: 'driver_signature', value: firstStopSigUrl })
      });
      if (attRes.ok) {
        const newAtt = await attRes.json();
        setStopAttachments(prev => [...prev, newAtt]);
        setAllJobAttachments(prev => [...prev, newAtt]);
        setActiveActionModal(null);
      }
    } catch (err) {
      console.error('Error copying driver signature:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachmentId) => {
    try {
      const res = await apiFetch(`/api/attachments/${attachmentId}`, { method: 'DELETE' });
      if (res.ok) {
        setStopAttachments(prev => prev.filter(a => a.id !== attachmentId));
        setAllJobAttachments(prev => prev.filter(a => a.id !== attachmentId));
      }
    } catch (err) {
      console.error('Error deleting attachment:', err);
    }
  };

  // Create validated return ticket
  const handleSaveReturnTicket = async () => {
    if (!activeStopModal) return;
    const activeLines = ticketLines.filter(l => (l.amount || 0) !== 0);
    if (activeLines.length === 0) {
      alert('Veuillez renseigner au moins une quantité pour un objet ou article.');
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/stops/${activeStopModal.id}/return-tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: ticketOrderId || null,
          lines: activeLines
        })
      });
      if (res.ok) {
        const created = await res.json();
        setStopReturnTickets(prev => [...prev, created]);
        setTicketLines([]);
        setTicketOrderId('');
        setActiveActionModal(null);
      }
    } catch (err) {
      console.error('Error saving return ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: '#090d16', minHeight: '100vh', width: '100%', color: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif', display: 'flex', justifyContent: 'center', padding: '0 0 60px 0' }}>
      <div style={{ width: '100%', maxWidth: '540px', display: 'flex', flexDirection: 'column', background: '#0f172a', minHeight: '100vh', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }}>
        
        {/* DRIVER INFO HEADER */}
        <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#38bdf8', color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '1.1rem' }}>
              {driver?.first_name ? driver.first_name[0] : 'C'}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: '#ffffff' }}>
                {driver?.first_name} {driver?.last_name}
              </h2>
              <div style={{ fontSize: '0.74rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                <span>📞 {driver?.phone || '+41 79 123 45 67'}</span>
                <span style={{ background: 'rgba(56, 189, 248, 0.2)', color: '#38bdf8', padding: '1px 6px', borderRadius: '4px', fontWeight: '700' }}>
                  Permis {driver?.driving_license || 'B'}
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
            <span style={{ fontSize: '0.72rem', color: wsConnected ? '#22c55e' : '#f59e0b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: wsConnected ? '#22c55e' : '#f59e0b' }}></span>
              {wsConnected ? 'En ligne (Live)' : 'Reconnexion...'}
            </span>

            {onSwitchServerOrDriver && (
              <button
                type="button"
                onClick={onSwitchServerOrDriver}
                style={{
                  background: 'rgba(56, 189, 248, 0.1)',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  color: '#38bdf8',
                  borderRadius: '6px',
                  padding: '2px 8px',
                  fontSize: '0.68rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Changer de serveur ou de profil chauffeur"
              >
                <Settings size={12} />
                <span>Changer</span>
              </button>
            )}
          </div>
        </div>

        {/* DATE SELECTOR BAR */}
        <div style={{
          background: '#131d31',
          borderBottom: '1px solid #334155',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: '#0f172a',
            border: '1px solid #334155',
            borderRadius: '8px',
            padding: '4px 8px',
            gap: '8px',
            flex: 1,
            justifyContent: 'space-between',
            position: 'relative'
          }}>
            <button
              type="button"
              onClick={handlePrevDay}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
              title="Jour précédent"
            >
              <ChevronLeft size={16} />
            </button>

            <div
              onClick={() => setShowDatePicker(!showDatePicker)}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: '800',
                fontSize: '0.86rem',
                color: '#ffffff',
                userSelect: 'none'
              }}
              title="Cliquer pour choisir une date"
            >
              <Calendar size={14} color="#38bdf8" />
              <span>{formatDateFormatted(currentDate)}</span>
            </div>

            <button
              type="button"
              onClick={handleNextDay}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
              title="Jour suivant"
            >
              <ChevronRight size={16} />
            </button>

            {showDatePicker && (
              <input
                type="date"
                value={formatDateISO(currentDate)}
                onChange={(e) => {
                  if (e.target.value) {
                    const parts = e.target.value.split('-');
                    setCurrentDate(new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])));
                    setSelectedJobId(null);
                  }
                  setShowDatePicker(false);
                }}
                onBlur={() => setShowDatePicker(false)}
                autoFocus
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: '6px',
                  background: '#1e293b',
                  color: '#ffffff',
                  border: '1px solid #38bdf8',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  fontSize: '0.84rem',
                  zIndex: 1000,
                  outline: 'none',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
                }}
              />
            )}
          </div>

          {formatDateISO(currentDate) !== formatDateISO(new Date()) && (
            <button
              type="button"
              onClick={handleToday}
              style={{
                marginLeft: '10px',
                background: 'rgba(56, 189, 248, 0.15)',
                border: '1px solid #38bdf8',
                color: '#38bdf8',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '0.72rem',
                fontWeight: '800',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Aujourd'hui
            </button>
          )}
        </div>

        {/* NON-BLOCKING PERSISTENT PAUSE BANNER */}
        {isPaused && (
          <div style={{
            background: 'linear-gradient(90deg, #f59e0b, #d97706)',
            color: '#0f172a',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.35)',
            position: 'sticky',
            top: 0,
            zIndex: 1000
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Pause size={18} fill="#0f172a" />
              <div>
                <b style={{ fontSize: '0.84rem' }}>Pause en cours :</b>
                <span style={{ fontSize: '0.9rem', fontWeight: '900', marginLeft: '6px', fontFamily: 'monospace' }}>
                  {formatTimerHHMMSS(pauseElapsedSec)}
                </span>
              </div>
            </div>

            <button
              onClick={handleTogglePause}
              style={{
                background: '#0f172a',
                color: '#ffffff',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontWeight: '900',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer'
              }}
            >
              <PlayCircle size={14} color="#22c55e" />
              <span>Reprendre</span>
            </button>
          </div>
        )}

        {/* HOME PAGE: LIST OF ASSIGNED JOBS */}
        {!selectedJobId && (
          <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            
            {/* DAILY STATS BANNER */}
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: '800', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                📊 Statistiques de la Journée
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', textAlign: 'center' }}>
                <div style={{ background: '#0f172a', padding: '10px 6px', borderRadius: '8px', border: '1px solid #334155' }}>
                  <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Distance Est.</div>
                  <div style={{ fontSize: '1rem', fontWeight: '900', color: '#22c55e', marginTop: '2px' }}>{totalDriverKm.toFixed(1)} km</div>
                </div>

                <div style={{ background: '#0f172a', padding: '10px 6px', borderRadius: '8px', border: '1px solid #334155' }}>
                  <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Stops Livrés</div>
                  <div style={{ fontSize: '1rem', fontWeight: '900', color: '#38bdf8', marginTop: '2px' }}>{completedDriverStops.length} / {allDriverStops.length}</div>
                </div>

                <div style={{ background: '#0f172a', padding: '10px 6px', borderRadius: '8px', border: '1px solid #334155' }}>
                  <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Durée Est.</div>
                  <div style={{ fontSize: '1rem', fontWeight: '900', color: '#f59e0b', marginTop: '2px' }}>{hours}h {mins}m</div>
                </div>
              </div>
            </div>

            {/* JOBS LIST */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Tournées du {formatDateFormatted(currentDate)} ({driverJobs.length})
              </div>

              {driverJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b', background: '#1e293b', borderRadius: '12px', border: '1px solid #334155' }}>
                  <Truck size={36} color="#475569" style={{ marginBottom: '8px' }} />
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#94a3b8' }}>Aucune tournée pour le {formatDateFormatted(currentDate)}</div>
                </div>
              ) : (
                driverJobs.map(job => {
                  const jobStops = stops.filter(s => s.job_id === job.id);
                  const round = rounds.find(r => r.id === job.round_id);
                  const truck = trucks.find(t => t.id === job.truck_id);
                  const startHub = hubs.find(h => h.id === job.start_hub_id) || hubs[0] || { name: 'Dépôt Principal' };
                  const endHub = hubs.find(h => h.id === job.end_hub_id) || startHub;
                  const payloadStats = calculateTruckPeakPayload(truck, jobStops);

                  const displayKm = parseFloat(job.estimated_length_km || 0);
                  const displaySec = parseInt(job.estimated_duration_sec || 0);
                  const displayCost = parseFloat(job.estimated_cost || 0);
                  const calculatedReturnTime = calculateReturnTime(job.estimated_departure || '07:30', displaySec);

                  let statusHeaderBg = '#1e293b';
                  let statusText = 'À planifier';
                  let statusColor = '#f59e0b';
                  if (job.status === 'in_progress') {
                    statusHeaderBg = 'rgba(56, 189, 248, 0.25)';
                    statusText = 'En cours';
                    statusColor = '#38bdf8';
                  } else if (job.status === 'completed' || job.status === 'done') {
                    statusHeaderBg = 'rgba(34, 197, 94, 0.25)';
                    statusText = 'Clôturée';
                    statusColor = '#22c55e';
                  }

                  return (
                    <div
                      key={job.id}
                      onClick={() => { setSelectedJobId(job.id); setViewMode('list'); }}
                      style={{
                        background: '#1e293b',
                        border: `1px solid ${job.status === 'in_progress' ? '#38bdf8' : '#334155'}`,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <div
                        style={{
                          background: statusHeaderBg,
                          borderLeft: `5px solid ${truck?.color || '#38bdf8'}`,
                          padding: '12px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderBottom: '1px solid #334155'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: '800', fontSize: '0.95rem', color: '#ffffff' }}>
                            {job.display_name}
                          </span>
                          <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                            {round ? `${round.name} (${round.code})` : 'Tournée sur-mesure'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.72rem', fontWeight: '800', color: statusColor, background: '#0f172a', padding: '2px 8px', borderRadius: '6px', border: '1px solid #334155' }}>
                            {statusText}
                          </span>
                          <ChevronRight size={16} color="#94a3b8" />
                        </div>
                      </div>

                      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', color: '#cbd5e1' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Truck size={14} color={truck?.color || '#38bdf8'} />
                            <span>{truck?.name || 'Véhicule standard'} ({truck?.plate || '-'})</span>
                          </div>
                          <span style={{ color: '#38bdf8', fontWeight: '700' }}>{jobStops.length} stops</span>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '0.72rem', background: '#0f172a', padding: '8px 10px', borderRadius: '8px', border: '1px solid #334155' }}>
                          <div>
                            <span style={{ color: '#94a3b8', display: 'block' }}>Départ :</span>
                            <b style={{ color: '#cbd5e1' }}>{job.estimated_departure || '07:30'}</b>
                          </div>
                          <div>
                            <span style={{ color: '#94a3b8', display: 'block' }}>Retour :</span>
                            <b style={{ color: '#cbd5e1' }}>{calculatedReturnTime}</b>
                          </div>
                          <div>
                            <span style={{ color: '#94a3b8', display: 'block' }}>Distance :</span>
                            <b style={{ color: '#22c55e' }}>{displayKm.toFixed(1)} km</b>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

          </div>
        )}

        {/* MISSION DETAIL VIEW */}
        {selectedJobId && currentJob && (() => {
          const truck = trucks.find(t => t.id === currentJob.truck_id);
          const startHub = hubs.find(h => h.id === currentJob.start_hub_id) || hubs[0] || { name: 'Dépôt Principal' };
          const endHub = hubs.find(h => h.id === currentJob.end_hub_id) || startHub;
          const displaySec = parseInt(currentJob.estimated_duration_sec || 0);
          const calculatedReturnTime = calculateReturnTime(currentJob.estimated_departure || '07:30', displaySec);

          return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              
              {/* TOP MISSION HEADER */}
              <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button
                  onClick={() => setSelectedJobId(null)}
                  style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: '700', padding: 0 }}
                >
                  <ArrowLeft size={16} />
                  <span>Mes Tournées</span>
                </button>

                {/* MAP / LIST VIEW TOGGLE BUTTON */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
                    style={{
                      background: viewMode === 'map' ? '#38bdf8' : '#0f172a',
                      color: viewMode === 'map' ? '#0f172a' : '#cbd5e1',
                      border: '1px solid #334155',
                      padding: '5px 10px',
                      borderRadius: '6px',
                      fontWeight: '800',
                      fontSize: '0.72rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {viewMode === 'list' ? <Map size={13} /> : <List size={13} />}
                    <span>{viewMode === 'list' ? 'Vue Carte' : 'Vue Liste'}</span>
                  </button>

                  {/* PAUSE BUTTON */}
                  {currentJob.status === 'in_progress' && (
                    <button
                      onClick={handleTogglePause}
                      style={{
                        background: isPaused ? '#ef4444' : '#f59e0b',
                        color: '#0f172a',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '6px',
                        fontWeight: '900',
                        fontSize: '0.72rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      {isPaused ? <PlayCircle size={13} /> : <Pause size={13} fill="#0f172a" />}
                      <span>{isPaused ? 'Terminer Pause' : 'Mettre en Pause'}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* MISSION ACTION BAR (START / KM / FINISH) */}
              <div style={{ padding: '8px 14px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.74rem' }}>
                  <span style={{ color: '#94a3b8' }}>Statut :</span>
                  <span style={{ fontWeight: '800', color: currentJob.status === 'in_progress' ? '#38bdf8' : currentJob.status === 'completed' ? '#22c55e' : '#f59e0b' }}>
                    {currentJob.status === 'in_progress' ? 'En Cours' : currentJob.status === 'completed' ? 'Clôturée' : 'À démarrer'}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {/* REAL START KM INPUT */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '3px 8px' }}>
                    <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: '600' }}>Km début :</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="0.0"
                      value={startKm}
                      onChange={e => setStartKm(e.target.value)}
                      onBlur={() => handleUpdateJobKm('real_start_km', startKm)}
                      style={{ width: '65px', background: 'transparent', border: 'none', color: '#38bdf8', fontSize: '0.74rem', fontWeight: '700', outline: 'none' }}
                      title="Kilométrage du véhicule au départ"
                    />
                  </div>

                  {currentJob.status === 'planned' && (
                    <button
                      disabled={loading}
                      onClick={handleStartMission}
                      style={{ background: '#38bdf8', color: '#0f172a', border: 'none', padding: '6px 12px', borderRadius: '6px', fontWeight: '900', fontSize: '0.74rem', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}
                    >
                      <Play size={13} fill="#0f172a" />
                      <span>Démarrer</span>
                    </button>
                  )}

                  {/* REAL END KM INPUT */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '3px 8px' }}>
                    <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: '600' }}>Km fin :</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="0.0"
                      value={endKm}
                      onChange={e => setEndKm(e.target.value)}
                      onBlur={() => handleUpdateJobKm('real_end_km', endKm)}
                      style={{ width: '65px', background: 'transparent', border: 'none', color: '#22c55e', fontSize: '0.74rem', fontWeight: '700', outline: 'none' }}
                      title="Kilométrage du véhicule au retour"
                    />
                  </div>

                  {currentJob.status === 'in_progress' && allJobStopsValidated && (
                    <button
                      disabled={loading}
                      onClick={handleFinishMission}
                      style={{ background: '#22c55e', color: '#ffffff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontWeight: '900', fontSize: '0.74rem', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}
                    >
                      <Flag size={13} />
                      <span>Clôturer</span>
                    </button>
                  )}

                  {startKm !== '' && endKm !== '' && !isNaN(parseFloat(startKm)) && !isNaN(parseFloat(endKm)) && parseFloat(endKm) >= parseFloat(startKm) && (
                    <span style={{ fontSize: '0.7rem', color: '#22c55e', fontWeight: '700', background: 'rgba(34, 197, 94, 0.15)', border: '1px solid #22c55e', borderRadius: '4px', padding: '2px 6px' }}>
                      {(parseFloat(endKm) - parseFloat(startKm)).toFixed(1)} km
                    </span>
                  )}
                </div>
              </div>

              {/* MAP VIEW TAB */}
              {viewMode === 'map' && (
                <div style={{ flex: 1, minHeight: '450px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                  <MapView
                    hubs={hubs.filter(h => h.id === currentJob.start_hub_id || h.id === currentJob.end_hub_id)}
                    stops={currentJobStops}
                    selectedStopId={activeStopModal?.id}
                    onSelectStop={(stopId) => {
                      const st = currentJobStops.find(s => s.id === stopId);
                      if (st) handleOpenStopModal(st);
                    }}
                  />
                  <div style={{ position: 'absolute', bottom: '12px', left: '12px', background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(4px)', padding: '6px 10px', borderRadius: '8px', border: '1px solid #334155', fontSize: '0.7rem', color: '#94a3b8' }}>
                    💡 Cliquez sur un marqueur pour ouvrir la fiche stop
                  </div>
                </div>
              )}

              {/* LIST VIEW TAB */}
              {viewMode === 'list' && (
                <div style={{ flex: 1, padding: '10px 14px 40px 14px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <tbody>
                      {/* DEPARTURE HUB ROW */}
                      <tr style={{ background: '#0f172a', borderBottom: '1px solid #334155' }}>
                        <td colSpan={4} style={{ padding: '8px 10px', color: '#cbd5e1' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Building size={14} color="#38bdf8" />
                              <span>Départ : <b>{startHub.name}</b></span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#1e293b', border: '1px solid #334155', borderRadius: '4px', padding: '1px 6px' }}>
                                <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Km début:</span>
                                <input
                                  type="number"
                                  step="0.1"
                                  placeholder="0.0"
                                  value={startKm}
                                  onChange={e => setStartKm(e.target.value)}
                                  onBlur={() => handleUpdateJobKm('real_start_km', startKm)}
                                  style={{ width: '55px', background: 'transparent', border: 'none', color: '#38bdf8', fontSize: '0.72rem', fontWeight: '700', outline: 'none' }}
                                />
                              </div>
                              <b style={{ color: '#38bdf8' }}>{currentJob.real_departure ? String(currentJob.real_departure).slice(11, 16) : (currentJob.estimated_departure || '07:30')}</b>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* TRAJET START HUB -> FIRST STOP & PAUSES */}
                      {currentJobStops.length > 0 && (() => {
                        const firstStop = currentJobStops[0];
                        const isJobClosed = currentJob.status === 'completed' || currentJob.status === 'done';
                        const isFirstStopDone = firstStop.status === 'delivered' || firstStop.status === 'failed';
                        const pausesBeforeFirst = (jobPauses || []).filter(p => {
                          if (parseInt(p.job_id) !== parseInt(currentJob.id)) return false;
                          if (parseInt(p.before_stop_id) !== parseInt(firstStop.id)) return false;
                          const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                          if (isReal) return true;
                          return !isJobClosed && !isFirstStopDone;
                        });
                        const firstPauseMins = pausesBeforeFirst.reduce((acc, p) => acc + parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10), 0);
                        return (
                          <>
                            <tr style={{ background: '#090d16' }}>
                              <td colSpan={4} style={{ padding: '4px 10px', borderBottom: '1px dashed #334155', color: '#94a3b8', fontSize: '0.7rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <Truck size={12} color="#94a3b8" />
                                  <span>Trajet : {calcHubToStopDriveMins(startHub, firstStop, currentJob.estimated_departure, firstPauseMins)} min ({calcHaversineDistanceKm(startHub.latitude, startHub.longitude, firstStop.latitude, firstStop.longitude)} km)</span>
                                </div>
                              </td>
                            </tr>
                            {pausesBeforeFirst.map(p => {
                              const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                              const pauseDuration = parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10);
                              const timeWindowStr = p.start && p.end ? `(${p.start} - ${p.end})` : '';
                              return (
                                <tr key={`pause-first-${p.id}`} style={{ background: '#0f172a' }}>
                                  <td colSpan={4} style={{ padding: '4px 10px', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                                      <Pause size={13} color="#94a3b8" />
                                      <span>
                                        {isReal ? (
                                          <>
                                            <span>Pause réelle : </span>
                                            <b style={{ color: '#22c55e', fontWeight: '700' }}>{pauseDuration} min</b>
                                            {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                          </>
                                        ) : (
                                          <>
                                            <span>Pause : </span>
                                            <b style={{ color: '#94a3b8', fontWeight: '500' }}>{pauseDuration} min</b>
                                            {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                          </>
                                        )}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        );
                      })()}

                      {/* ALL STOPS */}
                      {currentJobStops.map((stop, idx) => {
                        const client = clients.find(c => c.id === stop.client_id);
                        const isDoneStop = stop.status === 'delivered' || stop.status === 'failed';
                        const isFailedStop = stop.status === 'failed';
                        const stopDisplayNum = stop.real_index || stop.estimated_index || (idx + 1);

                        const arrivalTimeFormatted = stop.estimated_arrival ? stop.estimated_arrival.slice(0, 5) : '08:00';
                        const serviceMins = stop.service_time_mins !== undefined ? parseInt(stop.service_time_mins) : 10;
                        const departureTimeFormatted = calcDepartureTimeStr(arrivalTimeFormatted, serviceMins);

                        // Stop timings 4-state logic (Real = Green, Estimated/Unexecuted = Grey)
                        const hasRealArrival = !!stop.actual_arrival;

                        let arrivalDisplay = arrivalTimeFormatted;
                        let arrivalColor = '#94a3b8';
                        let arrivalWeight = '500';
                        let arrivalTitle = "Heure d'arrivée estimée";

                        let serviceDisplay = `${serviceMins}m`;
                        let serviceColor = '#94a3b8';
                        let serviceWeight = '500';
                        let serviceTitle = "Temps de service estimé";

                        let departureDisplay = departureTimeFormatted;
                        let departureColor = '#64748b';
                        let departureWeight = '500';
                        let departureTitle = "Heure de départ estimée";

                        if (hasRealArrival && !isDoneStop) {
                          // State 2: marked as arrived and nothing else -> Green real arrival, Grey estimated service & departure
                          arrivalDisplay = formatLocalTimeHHMM(stop.actual_arrival);
                          arrivalColor = '#22c55e';
                          arrivalWeight = '700';
                          arrivalTitle = "Heure d'arrivée réelle";

                          serviceColor = '#94a3b8';
                          serviceWeight = '500';
                          serviceTitle = "Temps de service estimé";

                          departureColor = '#64748b';
                          departureWeight = '500';
                          departureTitle = "Heure de départ estimée";
                        } else if (!hasRealArrival && isDoneStop) {
                          // State 3: validated and never marked as arrived -> Grey estimated arrival & service, Green real departure
                          arrivalDisplay = arrivalTimeFormatted;
                          arrivalColor = '#94a3b8';
                          arrivalWeight = '500';
                          arrivalTitle = "Heure d'arrivée estimée";

                          serviceColor = '#94a3b8';
                          serviceWeight = '500';
                          serviceTitle = "Temps de service estimé";

                          departureDisplay = stop.real_departure ? formatLocalTimeHHMM(stop.real_departure) : departureTimeFormatted;
                          departureColor = '#22c55e';
                          departureWeight = '700';
                          departureTitle = "Heure de départ réelle";
                        } else if (hasRealArrival && isDoneStop) {
                          // State 4: both marked as arrived and validated -> Green real arrival, Green real computed service, Green real departure
                          arrivalDisplay = formatLocalTimeHHMM(stop.actual_arrival);
                          arrivalColor = '#22c55e';
                          arrivalWeight = '700';
                          arrivalTitle = "Heure d'arrivée réelle";

                          let realServiceMins = serviceMins;
                          if (stop.real_departure && stop.actual_arrival) {
                            try {
                              let arrStr = String(stop.actual_arrival);
                              if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(arrStr)) arrStr = arrStr.replace(' ', 'T') + 'Z';
                              let depStr = String(stop.real_departure);
                              if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(depStr)) depStr = depStr.replace(' ', 'T') + 'Z';
                              const arrDate = new Date(arrStr);
                              const depDate = new Date(depStr);
                              const diffMs = depDate.getTime() - arrDate.getTime();
                              if (!isNaN(diffMs)) {
                                realServiceMins = Math.max(1, Math.round(diffMs / 60000));
                              }
                            } catch (e) {}
                          }
                          serviceDisplay = `${realServiceMins}m`;
                          serviceColor = '#22c55e';
                          serviceWeight = '700';
                          serviceTitle = "Temps de service réel";

                          departureDisplay = stop.real_departure ? formatLocalTimeHHMM(stop.real_departure) : departureTimeFormatted;
                          departureColor = '#22c55e';
                          departureWeight = '700';
                          departureTitle = "Heure de départ réelle";
                        } else {
                          // State 1: not arrived, still planned -> All values are estimated and grey
                          arrivalDisplay = arrivalTimeFormatted;
                          arrivalColor = '#94a3b8';
                          arrivalWeight = '500';
                          arrivalTitle = "Heure d'arrivée estimée";

                          serviceDisplay = `${serviceMins}m`;
                          serviceColor = '#94a3b8';
                          serviceWeight = '500';
                          serviceTitle = "Temps de service estimé";

                          departureDisplay = departureTimeFormatted;
                          departureColor = '#64748b';
                          departureWeight = '500';
                          departureTitle = "Heure de départ estimée";
                        }

                        return (
                          <React.Fragment key={stop.id}>
                            <tr
                              onClick={() => handleOpenStopModal(stop)}
                              style={{
                                background: '#1e293b',
                                borderBottom: '1px solid #334155',
                                cursor: 'pointer'
                              }}
                            >
                              {/* COL 1: TIMINGS (4 STATES DYNAMIC TIMINGS) */}
                              <td style={{ padding: '6px 6px', textAlign: 'center', width: '56px', verticalAlign: 'middle' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                                  <span style={{ fontSize: '0.72rem', fontWeight: arrivalWeight, color: arrivalColor }} title={arrivalTitle}>
                                    {arrivalDisplay}
                                  </span>
                                  <span style={{ fontSize: '0.66rem', fontWeight: serviceWeight, color: serviceColor }} title={serviceTitle}>
                                    {serviceDisplay}
                                  </span>
                                  <span style={{ fontSize: '0.7rem', fontWeight: departureWeight, color: departureColor }} title={departureTitle}>
                                    {departureDisplay}
                                  </span>
                                </div>
                              </td>

                              {/* COL 2: TIME WINDOWS */}
                              <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'middle' }}>
                                {renderStopRealTimeWindows(stop, client, arrivalTimeFormatted)}
                              </td>

                              {/* COL 3: CLIENT & ADDRESS */}
                              <td style={{ padding: '6px 6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span
                                    style={{
                                      background: isFailedStop ? '#ef4444' : isDoneStop ? '#22c55e' : '#38bdf8',
                                      color: isFailedStop ? '#ffffff' : '#0f172a',
                                      width: '18px',
                                      height: '18px',
                                      borderRadius: '50%',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: '800',
                                      fontSize: '0.7rem',
                                      flexShrink: 0
                                    }}
                                  >
                                    {stopDisplayNum}
                                  </span>
                                  <strong style={{ color: '#ffffff', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {stop.client_name}
                                  </strong>
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '1px' }}>
                                  {stop.address}, {stop.city}
                                </div>
                              </td>

                              {/* COL 4: WEIGHT & VOLUME */}
                              <td style={{ padding: '6px 6px', textAlign: 'right', verticalAlign: 'middle' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end' }}>
                                  <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                    {Math.round(stop.weight_kg || 0)} kg
                                  </span>
                                  <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                    {stop.volume_m3 || 0.5} m³
                                  </span>
                                </div>
                              </td>
                            </tr>

                            {/* INTER-STOP TRAJET & PAUSES */}
                            {idx < currentJobStops.length - 1 && (() => {
                              const nextStop = currentJobStops[idx + 1];
                              const isJobClosed = currentJob.status === 'completed' || currentJob.status === 'done';
                              const isNextStopDone = nextStop.status === 'delivered' || nextStop.status === 'failed';
                              const pausesForNextStop = (jobPauses || []).filter(p => {
                                if (parseInt(p.job_id) !== parseInt(currentJob.id)) return false;
                                if (parseInt(p.before_stop_id) !== parseInt(nextStop.id)) return false;
                                const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                                if (isReal) return true;
                                return !isJobClosed && !isNextStopDone;
                              });

                              const prevDepTime = stop.real_departure || stop.actual_arrival;
                              const nextArrTime = nextStop.actual_arrival;

                              let isRealTrajet = false;
                              let trajetMins = calcLegDriveTimeMins(stop, nextStop);

                              if (prevDepTime && nextArrTime) {
                                const tDep = new Date(prevDepTime).getTime();
                                const tArr = new Date(nextArrTime).getTime();
                                if (!isNaN(tDep) && !isNaN(tArr) && tArr >= tDep) {
                                  isRealTrajet = true;
                                  trajetMins = Math.max(1, Math.round((tArr - tDep) / 60000));
                                }
                              }

                              return (
                                <React.Fragment key={`leg-${stop.id}`}>
                                  <tr style={{ background: '#0f172a' }}>
                                    <td colSpan={4} style={{ padding: '4px 10px', borderTop: '1px dashed #334155', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                                        <Truck size={13} color="#94a3b8" />
                                        <span>Trajet : <b style={{ color: isRealTrajet ? '#22c55e' : '#94a3b8', fontWeight: isRealTrajet ? '700' : '400' }}>{trajetMins} min</b>&nbsp;&nbsp;&nbsp;<span style={{ color: '#94a3b8' }}>{calcHaversineDistanceKm(stop.latitude, stop.longitude, nextStop.latitude, nextStop.longitude)} km</span></span>
                                      </div>
                                    </td>
                                  </tr>

                                  {pausesForNextStop.map(p => {
                                    const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                                    const pauseDuration = parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10);
                                    const timeWindowStr = p.start && p.end ? `(${p.start} - ${p.end})` : '';
                                    return (
                                      <tr key={`pause-${p.id}`} style={{ background: '#0f172a' }}>
                                        <td colSpan={4} style={{ padding: '4px 10px', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                                            <Pause size={13} color="#94a3b8" />
                                            <span>
                                              {isReal ? (
                                                <>
                                                  <span>Pause réelle : </span>
                                                  <b style={{ color: '#22c55e', fontWeight: '700' }}>{pauseDuration} min</b>
                                                  {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                                </>
                                              ) : (
                                                <>
                                                  <span>Pause : </span>
                                                  <b style={{ color: '#94a3b8', fontWeight: '500' }}>{pauseDuration} min</b>
                                                  {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                                </>
                                              )}
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })()}
                          </React.Fragment>
                        );
                      })}

                      {/* TRAILING PAUSES */}
                      {(jobPauses || []).filter(p => {
                        if (parseInt(p.job_id) !== parseInt(currentJob.id)) return false;
                        if (currentJobStops.some(s => parseInt(s.id) === parseInt(p.before_stop_id))) return false;
                        const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                        if (isReal) return true;
                        const isJobClosed = currentJob.status === 'completed' || currentJob.status === 'done';
                        const hasPendingStops = currentJobStops.some(s => s.status === 'pending');
                        return !isJobClosed && hasPendingStops;
                      }).map(p => {
                        const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                        const pauseDuration = parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10);
                        const timeWindowStr = p.start && p.end ? `(${p.start} - ${p.end})` : '';
                        return (
                          <tr key={`pause-trailing-${p.id}`} style={{ background: '#0f172a' }}>
                            <td colSpan={4} style={{ padding: '4px 10px', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                                <Pause size={13} color="#94a3b8" />
                                <span>
                                  {isReal ? (
                                    <>
                                      <span>Pause réelle : </span>
                                      <b style={{ color: '#22c55e', fontWeight: '700' }}>{pauseDuration} min</b>
                                      {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                    </>
                                  ) : (
                                    <>
                                      <span>Pause : </span>
                                      <b style={{ color: '#94a3b8', fontWeight: '500' }}>{pauseDuration} min</b>
                                      {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                    </>
                                  )}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {/* TRAJET END HUB */}
                      {currentJobStops.length > 0 && (
                        <tr style={{ background: '#090d16' }}>
                          <td colSpan={4} style={{ padding: '5px 12px', borderTop: '1px dashed #334155', fontSize: '0.7rem', color: '#94a3b8' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Truck size={13} color="#94a3b8" />
                              <span>Trajet retour : <b>{calcStopToHubDriveMins(currentJobStops[currentJobStops.length - 1], endHub, calculatedReturnTime)} min ({calcHaversineDistanceKm(currentJobStops[currentJobStops.length - 1].latitude, currentJobStops[currentJobStops.length - 1].longitude, endHub.latitude, endHub.longitude)} km)</b></span>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* RETURN HUB ROW */}
                      <tr style={{ background: '#0f172a', borderTop: '1px solid #334155' }}>
                        <td colSpan={4} style={{ padding: '8px 12px', color: '#94a3b8', fontSize: '0.74rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Building size={14} color="#94a3b8" />
                              <span>Retour : <b style={{ color: currentJob.real_end ? '#22c55e' : '#cbd5e1' }}>{currentJob.real_end ? String(currentJob.real_end).slice(11, 16) : calculatedReturnTime}</b> ({endHub.name})</span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#1e293b', border: '1px solid #334155', borderRadius: '4px', padding: '1px 6px' }}>
                                <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Km fin:</span>
                                <input
                                  type="number"
                                  step="0.1"
                                  placeholder="0.0"
                                  value={endKm}
                                  onChange={e => setEndKm(e.target.value)}
                                  onBlur={() => handleUpdateJobKm('real_end_km', endKm)}
                                  style={{ width: '55px', background: 'transparent', border: 'none', color: '#22c55e', fontSize: '0.72rem', fontWeight: '700', outline: 'none' }}
                                />
                              </div>

                              {currentJob.status !== 'completed' && allJobStopsValidated && (
                                <button
                                  disabled={loading}
                                  onClick={handleFinishMission}
                                  style={{ background: '#22c55e', color: '#ffffff', border: 'none', padding: '4px 10px', borderRadius: '6px', fontWeight: '800', fontSize: '0.72rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                >
                                  <Flag size={12} />
                                  <span>Clôturer la Tournée</span>
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* ---------------------------------------------------- */}
              {/* UPGRADED STOP DETAIL & VALIDATION POPUP */}
              {/* ---------------------------------------------------- */}
              {activeStopModal && (() => {
                const client = clients.find(c => c.id === activeStopModal.client_id);
                const matchingContacts = clientContacts.filter(c => c.client_id === activeStopModal.client_id);
                const isValidated = activeStopModal.status === 'delivered' || activeStopModal.status === 'failed';
                const hasArrival = !!activeStopModal.actual_arrival;
                const isMissionStarted = currentJob && currentJob.status === 'in_progress';

                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '12px' }}>
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', width: '100%', maxWidth: '460px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                      
                      {/* MODAL HEADER */}
                      <div style={{ background: '#0f172a', padding: '14px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h3 style={{ margin: 0, fontSize: '1rem', color: '#ffffff', fontWeight: '800' }}>
                              {activeStopModal.client_name}
                            </h3>
                            <span style={{
                              fontSize: '0.68rem',
                              fontWeight: '800',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              background: activeStopModal.status === 'delivered'
                                ? 'rgba(34, 197, 94, 0.2)'
                                : (activeStopModal.status === 'failed' || selectedProblemReasons.length > 0)
                                  ? 'rgba(239, 68, 68, 0.2)'
                                  : 'rgba(56, 189, 248, 0.2)',
                              color: activeStopModal.status === 'delivered'
                                ? '#22c55e'
                                : (activeStopModal.status === 'failed' || selectedProblemReasons.length > 0)
                                  ? '#ef4444'
                                  : '#38bdf8'
                            }}>
                              {activeStopModal.status === 'delivered'
                                ? 'Livré'
                                : activeStopModal.status === 'failed'
                                  ? 'Échec'
                                  : selectedProblemReasons.length > 0
                                    ? '⚠️ Problème signalé'
                                    : 'En attente'}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                            {activeStopModal.address}, {activeStopModal.city}
                          </div>
                        </div>

                        <button onClick={() => setActiveStopModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                          <X size={18} />
                        </button>
                      </div>

                      {/* MODAL BODY (SCROLLABLE) */}
                      <div style={{ padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        
                        {/* NOT STARTED WARNING */}
                        {!isMissionStarted && (
                          <div style={{ background: 'rgba(245, 158, 11, 0.12)', border: '1px solid #f59e0b', borderRadius: '8px', padding: '8px 12px', color: '#f59e0b', fontSize: '0.74rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <AlertTriangle size={14} color="#f59e0b" />
                            <span>Tournée non démarrée : Démarrez la tournée pour marquer l'arrivée ou valider.</span>
                          </div>
                        )}

                        {/* ARRIVAL BUTTON */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0f172a', padding: '8px 12px', borderRadius: '8px', border: '1px solid #334155' }}>
                          <span style={{ fontSize: '0.76rem', color: '#cbd5e1' }}>Heure d'arrivée sur site :</span>
                          {hasArrival ? (
                            <span style={{ fontSize: '0.74rem', fontWeight: '700', color: '#22c55e', background: 'rgba(34, 197, 94, 0.15)', padding: '2px 8px', borderRadius: '4px' }}>
                              ✓ Arrivée à {formatLocalTimeHHMM(activeStopModal.actual_arrival)}
                            </span>
                          ) : (
                            <button
                              disabled={loading || isValidated || !isMissionStarted}
                              onClick={() => handleMarkArrival(activeStopModal.id)}
                              title={!isMissionStarted ? "Démarrer la tournée d'abord pour marquer l'arrivée" : "Marquer l'arrivée"}
                              style={{
                                background: (isValidated || !isMissionStarted) ? '#334155' : '#38bdf8',
                                color: (isValidated || !isMissionStarted) ? '#94a3b8' : '#0f172a',
                                border: 'none',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontWeight: '800',
                                fontSize: '0.74rem',
                                cursor: (loading || isValidated || !isMissionStarted) ? 'not-allowed' : 'pointer'
                              }}
                            >
                              📍 Marquer mon arrivée
                            </button>
                          )}
                        </div>

                        {/* INSTRUCTIONS */}
                        {activeStopModal.instructions && (
                          <div style={{ background: '#0f172a', padding: '8px 12px', borderRadius: '8px', fontSize: '0.75rem', color: '#94a3b8', border: '1px dashed #334155' }}>
                            📝 <b>Instructions :</b> {activeStopModal.instructions}
                          </div>
                        )}

                        {/* SIGNALED PROBLEMS BANNER */}
                        {selectedProblemReasons.length > 0 && (
                          <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444', borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <AlertTriangle size={16} color="#ef4444" />
                              <div>
                                <div style={{ fontSize: '0.76rem', fontWeight: '800', color: '#ef4444' }}>Motif(s) d'échec signalé(s) :</div>
                                <div style={{ fontSize: '0.72rem', color: '#fca5a5', marginTop: '2px' }}>
                                  {selectedProblemReasons.join(', ')}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                type="button"
                                onClick={() => setActiveActionModal('problem_reasons')}
                                style={{ background: '#1e293b', border: '1px solid #ef4444', color: '#ef4444', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '700', cursor: 'pointer' }}
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedProblemReasons([])}
                                style={{ background: 'none', border: 'none', color: '#94a3b8', padding: '4px', cursor: 'pointer' }}
                                title="Effacer le motif"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* PAYMENT TRACKING SECTION */}
                        <div style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <CreditCard size={14} />
                            <span>Paiement sur Place</span>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div>
                              <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Mode de paiement :</label>
                              <select
                                value={paymentType}
                                onChange={e => setPaymentType(e.target.value)}
                                style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '6px 8px', fontSize: '0.76rem', outline: 'none' }}
                              >
                                <option value="none">Aucun paiement (None)</option>
                                <option value="cash">Espèces (Cash)</option>
                                <option value="card">Carte bancaire (Card)</option>
                              </select>
                            </div>

                            <div>
                              <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Montant encaissé (CHF) :</label>
                              <input
                                type="number"
                                step="0.05"
                                placeholder="0.00"
                                value={amountPaid}
                                onChange={e => setAmountPaid(e.target.value)}
                                style={{ width: '100%', boxSizing: 'border-box', background: '#1e293b', border: '1px solid #334155', color: '#22c55e', fontWeight: '700', borderRadius: '6px', padding: '6px 8px', fontSize: '0.76rem', outline: 'none' }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* ATTACHMENTS & SIGNATURES LIST */}
                        <div style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#38bdf8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <ImageIcon size={14} />
                              <span>Pièces Jointes & Signatures ({stopAttachments.length})</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setActiveActionModal('action_menu')}
                              style={{
                                background: 'rgba(56, 189, 248, 0.15)',
                                border: '1px solid #38bdf8',
                                color: '#38bdf8',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.7rem',
                                fontWeight: '700',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                cursor: 'pointer'
                              }}
                              title="Ajouter une photo, signature ou document"
                            >
                              <Plus size={12} />
                              <span>Ajouter preuve</span>
                            </button>
                          </div>

                          {stopAttachments.length === 0 ? (
                            <div style={{ padding: '6px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.72rem', color: '#94a3b8' }}>
                              <span>Aucune pièce jointe enregistrée</span>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {stopAttachments.map(att => {
                                let label = 'Photo';
                                if (att.attachment_type === 'driver_signature') label = '✍️ Sig. Chauffeur';
                                else if (att.attachment_type === 'recipient_signature') label = '🖋️ Sig. Client';
                                else if (att.attachment_type === 'recipient_signature_name') label = `👤 Nom: ${att.value}`;
                                else if (att.attachment_type === 'proof_of_payment_photo') label = '💳 Preuve Paiement';
                                else if (att.attachment_type === 'order_pdf') label = '📄 PDF Bon';

                                return (
                                  <div key={att.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#1e293b', border: '1px solid #334155', padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem', color: '#cbd5e1' }}>
                                    {att.value && att.value.startsWith('/uploads/') && (
                                      <a href={att.value} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', color: '#38bdf8', textDecoration: 'none' }}>
                                        <img src={att.value} alt={label} style={{ width: '16px', height: '16px', borderRadius: '2px', objectFit: 'cover', marginRight: '4px' }} />
                                      </a>
                                    )}
                                    <span>{label}</span>
                                    <button onClick={() => handleDeleteAttachment(att.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 2px' }} title="Supprimer la pièce jointe">
                                      <X size={12} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* VALIDATED RETURN TICKETS */}
                        {stopReturnTickets.length > 0 && (
                          <div style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Package size={14} />
                                <span>Bons de Retour Validés ({stopReturnTickets.length})</span>
                              </div>
                              <span style={{ fontSize: '0.68rem', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <Lock size={11} /> Verrouillé
                              </span>
                            </div>

                            {stopReturnTickets.map(ticket => (
                              <div key={ticket.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem' }}>
                                  <span style={{ fontWeight: '700', color: '#cbd5e1' }}>
                                    Bon #{ticket.id} {ticket.order_id ? `(Réf: ${ticket.order_id})` : ''}
                                  </span>
                                  <span style={{ fontWeight: '800', color: '#22c55e' }}>
                                    Total: {parseFloat(ticket.validated_total_value || 0).toFixed(2)} CHF
                                  </span>
                                </div>
                                {ticket.lines && ticket.lines.map((l, lIdx) => (
                                  <div key={lIdx} style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>• {l.object_name || 'Objet'}</span>
                                    <span>{l.amount > 0 ? `+${l.amount}` : l.amount} × {parseFloat(l.unit_price || 0).toFixed(2)} CHF</span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* DRIVER NOTES */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>Remarques du livreur :</label>
                          <input
                            type="text"
                            placeholder="Notes ou détails sur la livraison..."
                            value={driverNotes}
                            onChange={(e) => setDriverNotes(e.target.value)}
                            style={{
                              background: '#0f172a',
                              border: '1px solid #334155',
                              color: '#fff',
                              padding: '8px 10px',
                              borderRadius: '8px',
                              fontSize: '0.78rem',
                              outline: 'none'
                            }}
                          />
                        </div>

                      </div>

                      {/* BOTTOM ACTION BAR */}
                      <div style={{ background: '#0f172a', padding: '12px 16px', borderTop: '1px solid #334155', display: 'flex', gap: '8px' }}>
                        {/* VALIDATE STOP BUTTON (GREEN OR RED IF PROBLEMS SIGNALED) */}
                        <button
                          disabled={loading || !isMissionStarted}
                          onClick={() => handleCompleteStop(activeStopModal.id, selectedProblemReasons.length > 0 ? 'failed' : 'delivered')}
                          title={!isMissionStarted ? "Démarrer la tournée d'abord pour pouvoir valider ce stop" : ""}
                          style={{
                            flex: 1,
                            background: !isMissionStarted ? '#334155' : (selectedProblemReasons.length > 0 ? '#ef4444' : '#22c55e'),
                            color: !isMissionStarted ? '#94a3b8' : '#ffffff',
                            border: 'none',
                            padding: '12px',
                            borderRadius: '10px',
                            fontWeight: '900',
                            fontSize: '0.86rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            cursor: (loading || !isMissionStarted) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {selectedProblemReasons.length > 0 ? (
                            <>
                              <AlertTriangle size={17} />
                              <span>{activeStopModal.status === 'failed' ? "✓ Échec Validé" : "Valider l'Échec"}</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle size={17} />
                              <span>{activeStopModal.status === 'delivered' ? '✓ Livraison Validée' : 'Valider Livraison'}</span>
                            </>
                          )}
                        </button>

                        {/* BLUE PLUS ACTION BUTTON */}
                        <button
                          disabled={loading || !isMissionStarted}
                          onClick={() => setActiveActionModal('action_menu')}
                          style={{
                            background: !isMissionStarted ? '#334155' : '#38bdf8',
                            color: !isMissionStarted ? '#94a3b8' : '#0f172a',
                            border: 'none',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            fontWeight: '900',
                            fontSize: '0.88rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: (loading || !isMissionStarted) ? 'not-allowed' : 'pointer'
                          }}
                          title={!isMissionStarted ? "Démarrer la tournée d'abord" : "Ajouter signature, photo, bon de retour..."}
                        >
                          <Plus size={18} />
                        </button>

                        {/* PROBLEM SIGNAL BUTTON (RED) */}
                        <button
                          disabled={loading || !isMissionStarted}
                          onClick={() => setActiveActionModal('problem_reasons')}
                          style={{
                            background: !isMissionStarted ? '#334155' : '#ef4444',
                            color: !isMissionStarted ? '#94a3b8' : '#ffffff',
                            border: 'none',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            fontWeight: '900',
                            fontSize: '0.88rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: (loading || !isMissionStarted) ? 'not-allowed' : 'pointer'
                          }}
                          title={!isMissionStarted ? "Démarrer la tournée d'abord" : "Signaler un problème / échec"}
                        >
                          <AlertTriangle size={17} />
                        </button>
                      </div>

                    </div>
                  </div>
                );
              })()}

              {/* ---------------------------------------------------- */}
              {/* ACTION MENU MODAL SHEET (BLUE + BUTTON) */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'action_menu' && activeStopModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                  <div style={{ background: '#1e293b', border: '1px solid #38bdf8', borderRadius: '14px', width: '100%', maxWidth: '400px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                    <div style={{ background: '#0f172a', padding: '14px 16px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '800', fontSize: '0.92rem', color: '#38bdf8' }}>
                        ➕ Ajouter une action pour ce stop
                      </span>
                      <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                        <X size={18} />
                      </button>
                    </div>

                    <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {/* OPTION 1: PHOTO */}
                      <button
                        onClick={() => { setActiveActionModal('photo'); setPhotoDataUrl(''); }}
                        style={{ background: '#0f172a', border: '1px solid #334155', padding: '12px 14px', borderRadius: '10px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <div style={{ background: 'rgba(56, 189, 248, 0.15)', padding: '8px', borderRadius: '8px' }}>
                          <Camera size={18} color="#38bdf8" />
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '0.84rem' }}>Preuve Photo / Document</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Colis, quai, preuve de paiement, bon papier</div>
                        </div>
                      </button>

                      {/* OPTION 2: DRIVER SIGNATURE */}
                      <button
                        onClick={() => setActiveActionModal('driver_sig')}
                        style={{ background: '#0f172a', border: '1px solid #334155', padding: '12px 14px', borderRadius: '10px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <div style={{ background: 'rgba(34, 197, 94, 0.15)', padding: '8px', borderRadius: '8px' }}>
                          <PenTool size={18} color="#22c55e" />
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '0.84rem' }}>Signature du Chauffeur</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Attestation de livraison effectuée par le livreur</div>
                        </div>
                      </button>

                      {/* OPTION 3: RECIPIENT SIGNATURE */}
                      <button
                        onClick={() => {
                          const clientContactsMatching = clientContacts.filter(c => c.client_id === activeStopModal.client_id);
                          setRecipientContactId(clientContactsMatching[0]?.id ? String(clientContactsMatching[0].id) : 'other');
                          setRecipientCustomName('');
                          setActiveActionModal('recipient_sig');
                        }}
                        style={{ background: '#0f172a', border: '1px solid #334155', padding: '12px 14px', borderRadius: '10px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <div style={{ background: 'rgba(245, 158, 11, 0.15)', padding: '8px', borderRadius: '8px' }}>
                          <User size={18} color="#f59e0b" />
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '0.84rem' }}>Signature du Client / Destinataire</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Sélection du contact client et signature tactile</div>
                        </div>
                      </button>

                      {/* OPTION 4: RETURN TICKET */}
                      <button
                        onClick={() => {
                          setTicketOrderId('');
                          setReturnTicketTab('returned');
                          setTicketLines(objects.map(o => ({ object_id: o.id, amount: 0, unit_price: parseFloat(o.price || 0) })));
                          setActiveActionModal('return_ticket');
                        }}
                        style={{ background: '#0f172a', border: '1px solid #334155', padding: '12px 14px', borderRadius: '10px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <div style={{ background: 'rgba(168, 85, 247, 0.15)', padding: '8px', borderRadius: '8px' }}>
                          <Package size={18} color="#a855f7" />
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '0.84rem' }}>Bon de Retour / Emballages & Articles</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Palettes consignées, emballages, modifs commande</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------- */}
              {/* PHOTO UPLOAD MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'photo' && activeStopModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                  <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', width: '100%', maxWidth: '420px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                    <div style={{ background: '#0f172a', padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#38bdf8' }}>📷 Téléverser une Photo / Preuve</span>
                      <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
                    </div>

                    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Type de document :</label>
                        <select
                          value={photoType}
                          onChange={e => setPhotoType(e.target.value)}
                          style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '8px', fontSize: '0.78rem' }}
                        >
                          <option value="other_photo">Photo colis / lieu de dépose</option>
                          <option value="proof_of_payment_photo">Preuve de paiement (reçu / chèque)</option>
                          <option value="other_document">Autre document / bon papier</option>
                        </select>
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = () => setPhotoDataUrl(reader.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />

                      {photoDataUrl ? (
                        <div style={{ textAlign: 'center' }}>
                          <img src={photoDataUrl} alt="Preview" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #334155' }} />
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            style={{ background: '#334155', color: '#cbd5e1', border: 'none', padding: '4px 10px', borderRadius: '4px', fontSize: '0.72rem', marginTop: '6px', cursor: 'pointer' }}
                          >
                            Changer l'image
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          style={{ border: '2px dashed #38bdf8', borderRadius: '10px', padding: '30px 20px', textAlign: 'center', cursor: 'pointer', background: '#0f172a' }}
                        >
                          <Camera size={32} color="#38bdf8" style={{ marginBottom: '6px' }} />
                          <div style={{ fontWeight: '700', fontSize: '0.82rem', color: '#ffffff' }}>Prendre une photo ou choisir un fichier</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '2px' }}>Appareil photo / galerie</div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                        <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', padding: '8px 14px', borderRadius: '6px', fontSize: '0.76rem', cursor: 'pointer' }}>Annuler</button>
                        <button
                          disabled={!photoDataUrl || loading}
                          onClick={() => handleUploadAndAttach(photoDataUrl, photoType, 'photo')}
                          style={{ background: photoDataUrl ? '#22c55e' : '#334155', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '800', fontSize: '0.76rem', cursor: photoDataUrl ? 'pointer' : 'not-allowed' }}
                        >
                          Enregistrer Photo
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------- */}
              {/* DRIVER SIGNATURE MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'driver_sig' && activeStopModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                  <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', width: '100%', maxWidth: '440px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                    <SignaturePadCanvas
                      title="✍️ Signature du Chauffeur"
                      subtitle={`${driver?.first_name} ${driver?.last_name}`}
                      onCancel={() => setActiveActionModal(null)}
                      onSave={(dataUrl) => handleUploadAndAttach(dataUrl, 'driver_signature', 'driver_sig')}
                      onCopyFirstStopSig={firstStopSigUrl ? handleCopyFirstStopSignature : null}
                    />
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------- */}
              {/* RECIPIENT SIGNATURE MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'recipient_sig' && activeStopModal && (() => {
                const matchingContacts = clientContacts.filter(c => c.client_id === activeStopModal.client_id);
                const selectedContact = matchingContacts.find(c => String(c.id) === recipientContactId);
                const effectiveSignerName = selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}` : recipientCustomName;

                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', width: '100%', maxWidth: '440px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                      <div style={{ padding: '14px 16px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#f59e0b' }}>🖋️ Signature Destinataire (Client)</span>
                          <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
                        </div>

                        {/* CONTACT DROPDOWN LOOKUP */}
                        <div>
                          <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Sélectionner le contact :</label>
                          <select
                            value={recipientContactId}
                            onChange={e => setRecipientContactId(e.target.value)}
                            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '6px 8px', fontSize: '0.76rem' }}
                          >
                            {matchingContacts.map(c => (
                              <option key={c.id} value={String(c.id)}>
                                {c.first_name} {c.last_name} {c.phone ? `(${c.phone})` : ''} {c.is_primary ? '★ Principal' : ''}
                              </option>
                            ))}
                            <option value="other">Autre / Nouveau signataire</option>
                          </select>
                        </div>

                        {recipientContactId === 'other' && (
                          <div>
                            <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Nom & prénom du signataire :</label>
                            <input
                              type="text"
                              placeholder="ex: Jean Dupont (Réceptionniste)..."
                              value={recipientCustomName}
                              onChange={e => setRecipientCustomName(e.target.value)}
                              style={{ width: '100%', boxSizing: 'border-box', background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '6px 8px', fontSize: '0.76rem' }}
                            />
                          </div>
                        )}
                      </div>

                      <SignaturePadCanvas
                        title="Pad de signature client"
                        subtitle={effectiveSignerName ? `Signataire: ${effectiveSignerName}` : ''}
                        onCancel={() => setActiveActionModal(null)}
                        onSave={async (dataUrl) => {
                          if (effectiveSignerName) {
                            try {
                              await fetch(`/api/stops/${activeStopModal.id}/attachments`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ attachment_type: 'recipient_signature_name', value: effectiveSignerName })
                              });
                            } catch (e) {}
                          }
                          await handleUploadAndAttach(dataUrl, 'recipient_signature', 'recipient_sig');
                        }}
                      />
                    </div>
                  </div>
                );
              })()}

              {/* ---------------------------------------------------- */}
              {/* TWO-TAB RETURN TICKET MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'return_ticket' && activeStopModal && (() => {
                const totalValue = ticketLines.reduce((acc, l) => acc + ((l.amount || 0) * (l.unit_price || 0)), 0);

                const returnedObjects = objects.filter(o => o.is_returned === true || o.is_returned === 'true' || o.is_returned === 1);
                const orderItemObjects = objects.filter(o => !o.is_returned || o.is_returned === false || o.is_returned === 'false' || o.is_returned === 0);

                const currentTabObjects = returnTicketTab === 'returned' ? returnedObjects : orderItemObjects;

                const handleAmountChange = (objId, delta) => {
                  setTicketLines(prev => {
                    const existing = prev.find(l => l.object_id === objId);
                    const obj = objects.find(o => o.id === objId);
                    const uPrice = parseFloat(obj?.price || 0);
                    if (existing) {
                      return prev.map(l => l.object_id === objId ? { ...l, amount: (l.amount || 0) + delta } : l);
                    } else {
                      return [...prev, { object_id: objId, amount: delta, unit_price: uPrice }];
                    }
                  });
                };

                const returnedLinesCount = ticketLines.filter(l => {
                  const o = returnedObjects.find(ro => ro.id === l.object_id);
                  return o && (l.amount || 0) !== 0;
                }).length;

                const orderLinesCount = ticketLines.filter(l => {
                  const o = orderItemObjects.find(oo => oo.id === l.object_id);
                  return o && (l.amount || 0) !== 0;
                }).length;

                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', width: '100%', maxWidth: '460px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                      <div style={{ padding: '12px 16px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#a855f7' }}>📦 Bon de Retour & Modifications Commande</span>
                        <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
                      </div>

                      {/* TWO TABS (RETURNED VS ORDER ITEMS) */}
                      <div style={{ display: 'flex', background: '#0f172a', borderBottom: '1px solid #334155', padding: '0 8px' }}>
                        <button
                          type="button"
                          onClick={() => setReturnTicketTab('returned')}
                          style={{
                            flex: 1,
                            background: returnTicketTab === 'returned' ? '#1e293b' : 'transparent',
                            color: returnTicketTab === 'returned' ? '#22c55e' : '#94a3b8',
                            border: 'none',
                            borderBottom: returnTicketTab === 'returned' ? '2px solid #22c55e' : 'none',
                            padding: '10px',
                            fontWeight: '800',
                            fontSize: '0.74rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <span>📦 Emballages / Consignes</span>
                          {returnedLinesCount > 0 && (
                            <span style={{ background: '#22c55e', color: '#0f172a', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem' }}>
                              {returnedLinesCount}
                            </span>
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => setReturnTicketTab('order_items')}
                          style={{
                            flex: 1,
                            background: returnTicketTab === 'order_items' ? '#1e293b' : 'transparent',
                            color: returnTicketTab === 'order_items' ? '#38bdf8' : '#94a3b8',
                            border: 'none',
                            borderBottom: returnTicketTab === 'order_items' ? '2px solid #38bdf8' : 'none',
                            padding: '10px',
                            fontWeight: '800',
                            fontSize: '0.74rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <span>🏷️ Articles Commande</span>
                          {orderLinesCount > 0 && (
                            <span style={{ background: '#38bdf8', color: '#0f172a', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem' }}>
                              {orderLinesCount}
                            </span>
                          )}
                        </button>
                      </div>

                      <div style={{ padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                          <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Référence / N° Commande (optionnel) :</label>
                          <input
                            type="text"
                            placeholder="ex: CMD-9842..."
                            value={ticketOrderId}
                            onChange={e => setTicketOrderId(e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '6px 8px', fontSize: '0.76rem' }}
                          />
                        </div>

                        <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                          {returnTicketTab === 'returned'
                            ? "Renseignez les emballages consignés (+ repris chez le client, - déchargés) :"
                            : "Renseignez les articles de la commande modifiés (+ ajoutés / retournés, - manquants) :"}
                        </div>

                        {currentTabObjects.length === 0 ? (
                          <div style={{ color: '#94a3b8', fontSize: '0.74rem', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                            Aucun élément enregistré dans cette catégorie.
                          </div>
                        ) : (
                          currentTabObjects.map(obj => {
                            const line = ticketLines.find(l => l.object_id === obj.id);
                            const amt = line ? line.amount : 0;
                            const uPrice = parseFloat(obj.price || 0);

                            return (
                              <div key={obj.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0f172a', padding: '8px 12px', borderRadius: '8px', border: '1px solid #334155' }}>
                                <div>
                                  <div style={{ fontWeight: '700', fontSize: '0.78rem', color: '#ffffff' }}>{obj.name}</div>
                                  <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                                    {uPrice.toFixed(2)} CHF / unité • {parseFloat(obj.weight_per_unit || 0).toFixed(1)} kg
                                  </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleAmountChange(obj.id, -1)}
                                    style={{ width: '26px', height: '26px', borderRadius: '4px', background: '#334155', color: '#fff', border: 'none', fontWeight: '900', cursor: 'pointer' }}
                                  >
                                    -
                                  </button>
                                  <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: '800', fontSize: '0.84rem', color: amt > 0 ? '#22c55e' : amt < 0 ? '#ef4444' : '#94a3b8' }}>
                                    {amt > 0 ? `+${amt}` : amt}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleAmountChange(obj.id, +1)}
                                    style={{ width: '26px', height: '26px', borderRadius: '4px', background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '900', cursor: 'pointer' }}
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}

                        <div style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.76rem', color: '#cbd5e1', fontWeight: '700' }}>Valeur Totale Validée :</span>
                          <span style={{ fontSize: '0.92rem', fontWeight: '900', color: totalValue !== 0 ? '#22c55e' : '#94a3b8' }}>
                            {totalValue.toFixed(2)} CHF
                          </span>
                        </div>
                      </div>

                      <div style={{ padding: '12px 16px', background: '#0f172a', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', padding: '8px 14px', borderRadius: '6px', fontSize: '0.76rem', cursor: 'pointer' }}>Annuler</button>
                        <button
                          disabled={loading}
                          onClick={handleSaveReturnTicket}
                          style={{ background: '#22c55e', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '800', fontSize: '0.76rem', cursor: 'pointer' }}
                        >
                          Valider le Bon de Retour
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ---------------------------------------------------- */}
              {/* PROBLEM REASONS MULTI-SELECT MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'problem_reasons' && activeStopModal && (() => {
                const toggleReason = (label) => {
                  setSelectedProblemReasons(prev =>
                    prev.includes(label) ? prev.filter(r => r !== label) : [...prev, label]
                  );
                };

                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                    <div style={{ background: '#1e293b', border: '1px solid #ef4444', borderRadius: '14px', width: '100%', maxWidth: '440px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                      <div style={{ padding: '12px 16px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#ef4444' }}>⚠️ Signaler un Problème / Échec</span>
                        <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
                      </div>

                      <div style={{ padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                          Sélectionnez une ou plusieurs raisons pour expliquer l'échec de la livraison :
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px' }}>
                          {commonProblemReasons.map(r => {
                            const isSelected = selectedProblemReasons.includes(r.label);
                            return (
                              <button
                                key={r.id}
                                type="button"
                                onClick={() => toggleReason(r.label)}
                                style={{
                                  background: isSelected ? 'rgba(239, 68, 68, 0.2)' : '#0f172a',
                                  border: `1px solid ${isSelected ? '#ef4444' : '#334155'}`,
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  color: isSelected ? '#ef4444' : '#cbd5e1',
                                  fontWeight: isSelected ? '800' : '500',
                                  fontSize: '0.76rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  cursor: 'pointer',
                                  textAlign: 'left'
                                }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span>{r.icon}</span>
                                  <span>{r.label}</span>
                                </span>
                                {isSelected && <Check size={14} color="#ef4444" />}
                              </button>
                            );
                          })}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                          <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>Remarques complémentaires :</label>
                          <textarea
                            rows={3}
                            placeholder="Détails supplémentaires..."
                            value={driverNotes}
                            onChange={e => setDriverNotes(e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '8px', fontSize: '0.76rem', resize: 'vertical' }}
                          />
                        </div>
                      </div>

                      <div style={{ padding: '12px 16px', background: '#0f172a', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', padding: '8px 14px', borderRadius: '6px', fontSize: '0.76rem', cursor: 'pointer' }}>Fermer</button>
                        <button
                          type="button"
                          onClick={() => setActiveActionModal(null)}
                          style={{
                            background: selectedProblemReasons.length > 0 ? '#ef4444' : '#334155',
                            color: '#ffffff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontWeight: '800',
                            fontSize: '0.76rem',
                            cursor: 'pointer'
                          }}
                        >
                          {selectedProblemReasons.length > 0 ? 'Enregistrer les motifs' : 'OK'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

            </div>
          );
        })()}

      </div>
    </div>
  );
}
